import React, { useCallback, useRef, useState } from 'react';

interface UploadZoneProps {
  label: string;
  sublabel: string;
  icon: string;
  fileName?: string;
  onFile: (file: File) => void;
  error?: string;
}

const UploadZone: React.FC<UploadZoneProps> = ({ label, sublabel, icon, fileName, onFile, error }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(
    (file: File) => {
      if (file) onFile(file);
    },
    [onFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleClick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const isLoaded = !!fileName;

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`upload-zone flex flex-col items-center justify-center gap-4 p-8 min-h-[220px] select-none
          ${dragOver ? 'drag-over' : ''}
          ${isLoaded ? 'loaded' : ''}
        `}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        aria-label={`Upload ${label} file`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".json,.html,.htm"
          onChange={handleChange}
          className="hidden"
          aria-hidden="true"
        />

        {isLoaded ? (
          <>
            <div className="text-4xl">✅</div>
            <div className="text-center">
              <p className="text-green-400 font-semibold text-base">{label} loaded!</p>
              <p className="text-white/40 text-sm mt-1 break-all max-w-[200px]">{fileName}</p>
            </div>
            <button
              className="text-xs text-white/40 underline hover:text-white/70 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
            >
              Change file
            </button>
          </>
        ) : (
          <>
            <div className="text-5xl">{icon}</div>
            <div className="text-center">
              <p className="text-white font-semibold text-lg">{label}</p>
              <p className="text-white/50 text-sm mt-1">{sublabel}</p>
            </div>
            <div className="flex items-center gap-2 text-white/30 text-xs">
              <span className="border border-white/20 rounded px-2 py-1">JSON</span>
              <span className="text-white/20">or</span>
              <span className="border border-white/20 rounded px-2 py-1">HTML</span>
            </div>
            <p className="text-white/30 text-xs">
              Drag & drop or <span className="text-white/60 underline">click to browse</span>
            </p>
          </>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-red-900/20 border border-red-500/30 rounded-lg px-3 py-2">
          <span className="text-red-400 text-sm mt-0.5">⚠</span>
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default UploadZone;
